import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Spinner } from "@/components/ui/spinner";
import { useMember } from "@/contexts/memberContext";
import { useLocationsQuery } from "@/hooks/useLocationsQuery";
import { usePermissibleLocationsQuery } from "@/hooks/usePermissibleLocationsQuery";
import { createMessageAPI } from "@/api/apiFactory";
import type { Member } from "@/types/Member";
import { getLocationLabel } from "@/lib/utils";

type AddNewLocationDialogProps = {
  memberToChange?: Member | null;
  isOpen: boolean;
  setIsAddNewLocationDialogOpen: (isOpen: boolean) => void;
};

const AddNewLocationDialog = ({ isOpen, setIsAddNewLocationDialogOpen }: AddNewLocationDialogProps) => {
  const { member } = useMember();
  const navigate = useNavigate();
  const [selectedLocationId, setSelectedLocationId] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const messageApi = useMemo(() => createMessageAPI(), []);

  // Fetch all locations
  const { data: allLocations = [], isLoading: isLoadingAllLocations } = useLocationsQuery();

  // Fetch user's current permissible locations
  const { data: permissibleLocations = [], isLoading: isLoadingPermissible } = usePermissibleLocationsQuery();

  // Filter out locations the user already has access to
  const availableLocations = useMemo(() => {
    const permissibleIds = new Set(permissibleLocations.map((loc) => loc.id));
    return allLocations
      .filter((loc) => !permissibleIds.has(loc.id))
      .sort((a, b) => getLocationLabel(a).localeCompare(getLocationLabel(b)));
  }, [allLocations, permissibleLocations]);

  const selectedLocation = useMemo(() => {
    return availableLocations.find((loc) => loc.id === selectedLocationId);
  }, [availableLocations, selectedLocationId]);

  const handleRequestAccess = async () => {
    if (!selectedLocationId || !selectedLocation || !member) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const locationName = getLocationLabel(selectedLocation);
      const messageContent = `Hello there, I am interested in ${locationName} and am looking to learn more.`;

      const { conversation } = await messageApi.requestLocationAccess(selectedLocationId, messageContent);

      setSuccess(true);
      setSelectedLocationId("");

      // When messaging is enabled the server returns a conversation to deep-link into; when
      // it's off, the PM email still went out — just show success and close.
      setTimeout(() => {
        setIsAddNewLocationDialogOpen(false);
        setSuccess(false);
        if (conversation) {
          navigate(`/messages?conversation=${encodeURIComponent(conversation.id)}`);
        }
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send request. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedLocationId("");
    setError(null);
    setSuccess(false);
    setIsAddNewLocationDialogOpen(false);
  };

  const isLoading = isLoadingAllLocations || isLoadingPermissible;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="text-3xl">Add a new location</DialogTitle>
          <DialogDescription>
            We have locations across the UK. If you would like to use another location, please select it here and the
            practice manager for that location will be notified and contact you to arrange access, a warm welcome and a
            viewing if you wish.
          </DialogDescription>
        </DialogHeader>

        {success ? (
          <div className="py-4 text-center text-green-600">
            Request sent! The practice manager will be in touch soon.
          </div>
        ) : (
          <>
            {isLoading ? (
              <div className="flex items-center justify-center py-4">
                <Spinner className="size-5 mr-2" />
                <span>Loading locations...</span>
              </div>
            ) : availableLocations.length === 0 ? (
              <div className="py-4 text-center text-muted-foreground">
                You already have access to all available locations.
              </div>
            ) : (
              <div className="py-2">
                <Select value={selectedLocationId} onValueChange={setSelectedLocationId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent className="max-h-[40vh] overflow-y-auto">
                    {availableLocations.map((location) => (
                      <SelectItem key={location.id} value={location.id}>
                        {getLocationLabel(location)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {error && <div className="text-sm text-red-600">{error}</div>}
          </>
        )}

        <DialogFooter>
          <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {!success && availableLocations.length > 0 && (
            <Button onClick={handleRequestAccess} disabled={!selectedLocationId || isSubmitting}>
              {isSubmitting ? (
                <>
                  <Spinner className="size-4 mr-2" />
                  Sending...
                </>
              ) : (
                "Request access"
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddNewLocationDialog;
